/**
 * Web Merchandising Benchmark Types
 *
 * Shared types and constants for the benchmarking feature across
 * Collection, Homepage, and Product Page funnels.
 */

/**
 * Benchmark timeframe options for historical comparison.
 * - 1mo: Past 1 month
 * - 3mo: Past 3 months
 * - 6mo: Past 6 months
 */
export type BenchmarkTimeframe = "1mo" | "3mo" | "6mo"

/**
 * Benchmark values for a single metric.
 */
export interface BenchmarkValues {
  /** Average value over the timeframe */
  avg: number
  /** Best 14-day value observed in the timeframe */
  best: number
  /** Worst 14-day value observed in the timeframe */
  worst: number
}

/**
 * Collection of benchmarks keyed by metric name.
 */
export interface FunnelBenchmarks {
  [metricKey: string]: BenchmarkValues
}

/**
 * Funnel types for benchmark data fetching.
 */
export type FunnelType = "collection" | "homepage" | "product"

/**
 * Dropdown options for benchmark timeframe selector.
 */
export const BENCHMARK_TIMEFRAME_OPTIONS = [
  { label: "Past 1 Month", value: "1mo" as const },
  { label: "Past 3 Months", value: "3mo" as const },
  { label: "Past 6 Months", value: "6mo" as const },
]

/**
 * Cube names for each funnel type's benchmarks.
 */
export const BENCHMARK_CUBE_NAMES: Record<FunnelType, string> = {
  collection: "company_web_analytics_collection_funnel_benchmarks",
  homepage: "company_web_analytics_homepage_funnel_benchmarks",
  product: "company_web_analytics_product_page_funnel_benchmarks",
}

/**
 * Metric key mappings for each funnel type.
 * Maps frontend metric keys to cube measure names.
 */
export const COLLECTION_BENCHMARK_METRICS = {
  collection_to_cart: "collection_to_cart_cvr",
  cart_to_purchase: "cart_to_purchase_cvr",
  collection_to_purchase: "collection_to_purchase_cvr",
} as const

export const HOMEPAGE_BENCHMARK_METRICS = {
  homepage_to_shopping: "homepage_to_shopping_cvr",
  homepage_to_cart: "homepage_to_cart_cvr",
  homepage_to_purchase: "homepage_to_purchase_cvr",
  shopping_to_cart: "shopping_to_cart_cvr",
  shopping_to_purchase: "shopping_to_purchase_cvr",
  cart_to_purchase: "cart_to_purchase_cvr",
} as const

export const PRODUCT_BENCHMARK_METRICS = {
  product_to_cart: "product_to_cart_cvr",
  product_to_checkout: "product_to_checkout_cvr",
  product_to_purchase: "product_to_purchase_cvr",
  cart_to_checkout: "cart_to_checkout_cvr",
  cart_to_purchase: "cart_to_purchase_cvr",
  checkout_to_purchase: "checkout_to_purchase_cvr",
} as const

/**
 * Plotted row for overlaying individual entity data on funnel charts.
 */
export interface PlottedRow {
  id: string
  label: string
  color: string
  stages: { label: string; value: number }[]
  compareStages?: { label: string; value: number }[]
}

export const PLOT_ROW_COLORS = ["#4ECDC4", "#FFD93D", "#6C5CE7"]
export const MAX_PLOTTED_ROWS = 3

/**
 * Color palette for the benchmark bar visualization.
 */
export const BENCHMARK_COLORS = {
  // Bar segments (left to right: worst to best)
  segmentRed: "#ef4444", // red-500 (poor zone)
  segmentOrange: "#f97316", // orange-500 (below avg)
  segmentLightGreen: "#86efac", // green-300 (above avg)
  segmentGreen: "#22c55e", // green-500 (best zone)

  // Markers and labels
  markerLine: "#ffffff", // white
  currentValueText: "#ffffff", // white
  benchmarkValueText: "#ffffff", // white
  benchmarkLabelText: "#9ca3af", // gray-400

  // Card background
  cardBackground: "bg-system-blue-900/50",
} as const
